import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'

interface NavigationTab {
  id: string
  label: string
  icon: string
  path: string
  badge?: {
    type: 'count' | 'dot' | 'urgent'
    value?: number
  }
  accessibilityLabel: string
}

const parentTabs: NavigationTab[] = [
  {
    id: 'dashboard',
    label: 'หน้าหลัก',
    icon: '🏠',
    path: '/dashboard',
    accessibilityLabel: 'หน้าหลัก ดูข้อมูลทั่วไป'
  },
  {
    id: 'payment',
    label: 'ชำระเงิน',
    icon: '💳',
    path: '/payment',
    // badge: { type: 'urgent' }, // if payment due - temporarily disabled for footer testing
    accessibilityLabel: 'ชำระค่าเรียน'
  },
  {
    id: 'lessons',
    label: 'บทเรียน',
    icon: '📚',
    path: '/lessons',
    // badge: { type: 'dot' }, // if new lesson - temporarily disabled for footer testing
    accessibilityLabel: 'ดูบทเรียนและการบ้าน'
  },
  {
    id: 'expenses',
    label: 'ค่าใช้จ่าย',
    icon: '🧾',
    path: '/expenses',
    // badge: { type: 'count', value: 3 }, // pending expenses - temporarily disabled for footer testing
    accessibilityLabel: 'ค่าใช้จ่ายอื่นๆ ที่รอชำระ'
  },
  {
    id: 'profile',
    label: 'โปรไฟล์',
    icon: '👤',
    path: '/profile',
    accessibilityLabel: 'ข้อมูลส่วนตัวและนักเรียน'
  }
]

const adminTabs: NavigationTab[] = [
  {
    id: 'dashboard',
    label: 'หน้าหลัก',
    icon: '🏠',
    path: '/admin/dashboard',
    accessibilityLabel: 'หน้าหลักผู้ดูแล'
  },
  {
    id: 'attendance',
    label: 'เช็คชื่อ',
    icon: '📋',
    path: '/admin/attendance',
    accessibilityLabel: 'เช็คชื่อนักเรียน'
  },
  {
    id: 'lessons',
    label: 'บทเรียน',
    icon: '📚',
    path: '/admin/lessons',
    accessibilityLabel: 'จัดการบทเรียน'
  },
  {
    id: 'payments',
    label: 'การชำระเงิน',
    icon: '💳',
    path: '/admin/payments',
    accessibilityLabel: 'ตรวจสอบการชำระเงิน'
  },
  {
    id: 'students',
    label: 'นักเรียน',
    icon: '👥',
    path: '/admin/students',
    accessibilityLabel: 'จัดการข้อมูลนักเรียน'
  }
]

interface TabBadgeProps {
  badge: {
    type: 'count' | 'dot' | 'urgent'
    value?: number
  }
}

const TabBadge: React.FC<TabBadgeProps> = ({ badge }) => {
  const getBadgeClasses = () => {
    switch (badge.type) {
      case 'urgent':
        return 'bg-red-500 animate-pulse'
      case 'count':
        return 'bg-red-500'
      case 'dot':
        return 'bg-blue-500'
      default:
        return 'bg-gray-500'
    }
  }
  
  if (badge.type === 'dot') {
    return (
      <div 
        className={clsx('nav-badge dot', getBadgeClasses())}
        aria-label="มีข้อมูลใหม่"
      />
    )
  }
  
  return (
    <div 
      className={clsx('nav-badge', getBadgeClasses())}
      aria-label={`${badge.value} รายการ`}
    >
      {badge.value}
    </div>
  )
}

interface TabNavigationProps {
  tabs?: NavigationTab[]
  activeTab?: string
}

// Export admin tabs for components that need specific tab configuration
export { adminTabs }

export const TabNavigation: React.FC<TabNavigationProps> = ({ tabs, activeTab }) => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Auto-detect admin vs parent tabs based on current path
  const isAdminRoute = location.pathname.startsWith('/admin')
  const defaultTabs = isAdminRoute ? adminTabs : parentTabs
  
  // Use custom tabs or auto-detected tabs
  const navigationTabs = tabs || defaultTabs
  
  const handleTabChange = (path: string) => {
    navigate(path)
  }
  
  return (
    <nav 
      className="nav-thai"
      role="tablist"
      aria-label="เมนูหลัก"
    >
      <div className="flex h-full">
        {navigationTabs.map((tab) => {
          const isActive = activeTab ? 
            tab.id === activeTab : 
            (location.pathname === tab.path || (tab.path === '/dashboard' && location.pathname === '/'))
          
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-label={`${tab.accessibilityLabel}${isActive ? ' - หน้าปัจจุบัน' : ''}`}
              className={clsx(
                'nav-item-thai',
                isActive && 'active'
              )}
              onClick={() => handleTabChange(tab.path)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleTabChange(tab.path)
                }
              }}
            >
              <div className="relative flex flex-col items-center">
                <span className="nav-icon" aria-hidden="true">
                  {tab.icon}
                </span>
                
                {tab.badge && (
                  <TabBadge badge={tab.badge} />
                )}
              </div>
              
              <span className="text-xs font-medium mt-1 leading-tight">
                {tab.label}
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}